import React, { useEffect, useRef, useState } from 'react';
import {
  BandPart,
  Cell,
  ColumnHeader,
  DayButton,
  DayNumberSpan,
  EmptyCell,
  Grid,
  GridDensity,
  Heading,
  Root,
  Row,
} from './month-grid.styled';
import {
  WEEKDAYS,
  Weekday,
  monthOf,
  monthTitle,
  monthWeeks,
  shiftDay,
  shiftMonthClamped,
  weekEnd,
  weekStart,
  weekdayOf,
} from './month-model';

/**
 * ONE MONTH OF BUILDING DAYS — the shared grid under the Stays calendar and
 * the date-range picker.
 *
 * Every day is a 'YYYY-MM-DD' key on the BUILDING's calendar (month-model.ts),
 * Monday first, and never a JS local Date. The grid draws the days and owns the
 * gestures; what a day SAYS (its body and its accessible name) comes from the
 * parent through renderDay and dayLabel.
 *
 * ARIA grid: one role=grid named by the parent's heading (headingId), a row of
 * column headers, weeks as rows, and ONE native button per day cell. Roving
 * tabindex — exactly one day is in the tab order: the focus cursor when it is
 * in this month, else today, else the 1st. The cursor itself may be owned by
 * the parent (focusDay + onFocusDayChange) so two grids side by side share one.
 *
 * KEYBOARD on a day: arrows ±1 day / ±1 week, Home and End the Monday and
 * Sunday of that week, PageUp and PageDown the same day a month away (clamped
 * to the month's length). All of them only ASK (onFocusDayChange) — the parent
 * decides which month is on screen; the grid then moves DOM focus to that day
 * when it is in this month. Enter and Space are the native button's click, so
 * they activate exactly like a tap. Shift+arrow extends a selection; Escape
 * asks the parent to cancel.
 *
 * POINTER: a mouse or pen press that MOVES more than 6px is a drag selection
 * (start, extend on each day entered, end on release) and the click that
 * follows it is swallowed. A press without that movement is a click. Touch
 * never drags — a finger on a calendar is a scroll or a tap — and the cells
 * carry touch-action: manipulation so a vertical swipe still scrolls the page.
 */

/** Which gesture moved the cursor: arrows, PageUp/PageDown, Home/End, a
 *  click or tap, or focus arriving on a day some other way (Tab). */
export type FocusCause = 'arrow' | 'page' | 'edge' | 'pointer' | 'focus';

export interface DayContext {
  inMonth: boolean;
  isToday: boolean;
  weekday: Weekday;
  /** One of the two ends of the selection. */
  selected: boolean;
  /** Anywhere from start to end, inclusive. */
  inSelection: boolean;
}

/** INCLUSIVE on screen: both ends are drawn as selected days. */
export interface DaySelection {
  start: string;
  end: string;
}

export interface MonthGridProps {
  /** 'YYYY-MM'. */
  month: string;
  /** Today on the building's calendar. */
  today: string;
  renderDay: (day: string, ctx: DayContext) => React.ReactNode;
  /** The day button's accessible name. It must contain the full date. */
  dayLabel: (day: string) => string;
  selectable?: boolean;
  selection?: DaySelection | null;
  onDayActivate?: (day: string) => void;
  onSelectionStart?: (day: string) => void;
  onSelectionExtend?: (day: string) => void;
  onSelectionEnd?: (day: string) => void;
  /** The focus cursor, when the parent owns it. */
  focusDay?: string;
  /** The cursor should move here. `cause` says which gesture asked ('page' is
   *  PageUp/PageDown, so a parent showing two months can move both) and
   *  `from` is the day the gesture started on. */
  onFocusDayChange?: (day: string, cause: FocusCause, from: string) => void;
  /** Escape on a day. */
  onEscape?: () => void;
  headingLevel?: 2 | 3;
  /** The grid is aria-labelledby this id. The parent renders that heading,
   *  unless showHeading asks the grid to draw the month title itself. */
  headingId: string;
  showHeading?: boolean;
  /** Draw the neighbouring months' days in the leading and trailing cells.
   *  Off by default: those cells are empty. */
  showOutsideDays?: boolean;
  density?: GridDensity;
  /** A day that cannot be chosen. It stays focusable (aria-disabled) so the
   *  keyboard can travel over it. */
  isDayDisabled?: (day: string) => boolean;
  /** False takes every day out of the tab order — for the second of two
   *  grids that share one cursor, so the pair is ONE tab stop. */
  inTabOrder?: boolean;
  className?: string;
}

/* ─────────────────────────────  the drag  ───────────────────────────── */

/* Movement before a mouse press counts as a drag rather than a click. */
export const DRAG_THRESHOLD_PX = 6;

interface DragHandlers {
  onSelectionStart?: (day: string) => void;
  onSelectionExtend?: (day: string) => void;
  onSelectionEnd?: (day: string) => void;
}

interface DragState {
  pointerId: number | undefined;
  startDay: string;
  lastDay: string;
  x: number;
  y: number;
  dragging: boolean;
  owner: React.MutableRefObject<DragHandlers>;
}

/* ONE drag at a time, across every grid on the page — a drag that starts in
   October may finish in November, which is the next grid along. */
let drag: DragState | null = null;
/* The click a browser fires after a drag that ends on a day is not an
   activation. Cleared on the next task, so it can only ever eat that one. */
let swallowClick = false;

const samePointer = (pointerId: number | undefined): boolean =>
  !!drag && (drag.pointerId === undefined || pointerId === undefined || drag.pointerId === pointerId);

const beginIfMoved = (x: number, y: number): void => {
  if (!drag || drag.dragging) return;
  if (Math.hypot(x - drag.x, y - drag.y) <= DRAG_THRESHOLD_PX) return;
  drag.dragging = true;
  drag.owner.current.onSelectionStart?.(drag.startDay);
  if (drag.lastDay !== drag.startDay) drag.owner.current.onSelectionExtend?.(drag.lastDay);
};

const endDrag = (): void => {
  window.removeEventListener('pointerup', endDrag);
  window.removeEventListener('pointercancel', endDrag);
  const finished = drag;
  drag = null;
  if (!finished || !finished.dragging) return;
  swallowClick = true;
  window.setTimeout(() => {
    swallowClick = false;
  }, 0);
  finished.owner.current.onSelectionEnd?.(finished.lastDay);
};

const isDragPointer = (pointerType: string): boolean =>
  pointerType === 'mouse' || pointerType === 'pen';

/* ─────────────────────────────  the grid  ───────────────────────────── */

const bandOf = (day: string, selection: DaySelection | null | undefined): BandPart => {
  if (!selection) return 'none';
  const lo = selection.start <= selection.end ? selection.start : selection.end;
  const hi = selection.start <= selection.end ? selection.end : selection.start;
  if (day < lo || day > hi) return 'none';
  if (lo === hi) return 'single';
  if (day === lo) return 'start';
  if (day === hi) return 'end';

  return 'middle';
};

/** The number of a day, in the span the grid rings for today. */
export const DayNumber: React.FC<{ day: string; className?: string }> = ({
  day,
  className,
}) => (
  <DayNumberSpan data-day-number="" className={className} aria-hidden="true">
    {Number(day.slice(8, 10))}
  </DayNumberSpan>
);

export const MonthGrid: React.FC<MonthGridProps> = props => {
  const {
    month,
    today,
    renderDay,
    dayLabel,
    selectable = false,
    selection = null,
    onDayActivate,
    onSelectionStart,
    onSelectionExtend,
    focusDay,
    onFocusDayChange,
    onEscape,
    headingLevel = 3,
    headingId,
    showHeading = false,
    showOutsideDays = false,
    density = 'picker',
    isDayDisabled,
    inTabOrder = true,
    className,
  } = props;

  const handlers = useRef<DragHandlers>(props);
  handlers.current = props;
  const gridRef = useRef<HTMLDivElement>(null);
  const [ownCursor, setOwnCursor] = useState<string | null>(null);
  const cursor = focusDay ?? ownCursor;

  const weeks = monthWeeks(month, { outside: showOutsideDays });
  const inThisMonth = (day: string | null | undefined): day is string =>
    !!day && monthOf(day) === month;
  const tabStop = !inTabOrder
    ? null
    : inThisMonth(cursor)
      ? cursor
      : inThisMonth(today)
        ? today
        : `${month}-01`;

  /* DOM focus follows the cursor — but only when focus is already on a day
     (or was lost because the day it sat on scrolled out of the pair). A
     cursor moved by "Clear dates" or a month button never steals focus. */
  const previousCursor = useRef(cursor);
  useEffect(() => {
    const previous = previousCursor.current;
    previousCursor.current = cursor;
    if (!cursor || cursor === previous || monthOf(cursor) !== month) return;
    const active = document.activeElement;
    const onADay =
      !active ||
      active === document.body ||
      (active instanceof HTMLElement && active.hasAttribute('data-day'));
    if (!onADay) return;
    const button = gridRef.current?.querySelector<HTMLButtonElement>(
      `[data-day="${cursor}"]`
    );
    if (button && button !== active) button.focus();
  }, [cursor, month]);

  const moveCursor = (day: string, cause: FocusCause, from: string) => {
    setOwnCursor(day);
    onFocusDayChange?.(day, cause, from);
  };

  const disabledDay = (day: string): boolean => !!isDayDisabled?.(day);

  const onKeyDown = (day: string, event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Escape') {
      if (onEscape) {
        event.preventDefault();
        onEscape();
      }

      return;
    }
    let target: string | null = null;
    let cause: FocusCause = 'arrow';
    switch (event.key) {
      case 'ArrowLeft':
        target = shiftDay(day, -1);
        break;
      case 'ArrowRight':
        target = shiftDay(day, 1);
        break;
      case 'ArrowUp':
        target = shiftDay(day, -7);
        break;
      case 'ArrowDown':
        target = shiftDay(day, 7);
        break;
      case 'Home':
        target = weekStart(day);
        cause = 'edge';
        break;
      case 'End':
        target = weekEnd(day);
        cause = 'edge';
        break;
      case 'PageUp':
        target = shiftMonthClamped(day, -1);
        cause = 'page';
        break;
      case 'PageDown':
        target = shiftMonthClamped(day, 1);
        cause = 'page';
        break;
      default:
        return;
    }
    event.preventDefault();
    if (event.shiftKey && selectable && cause === 'arrow') {
      if (!selection) onSelectionStart?.(day);
      onSelectionExtend?.(target);
    }
    moveCursor(target, cause, day);
  };

  const onPointerDown = (day: string, event: React.PointerEvent<HTMLButtonElement>) => {
    /* A new press: whatever click the last drag left behind is not this one. */
    swallowClick = false;
    if (!selectable || !isDragPointer(event.pointerType)) return;
    if (event.button !== 0 || disabledDay(day)) return;
    const button = event.currentTarget;
    /* A pen gets implicit pointer capture in some browsers, which would keep
       every later pointerenter on this one day. */
    if (
      typeof button.hasPointerCapture === 'function' &&
      typeof button.releasePointerCapture === 'function' &&
      event.pointerId !== undefined &&
      button.hasPointerCapture(event.pointerId)
    ) {
      button.releasePointerCapture(event.pointerId);
    }
    drag = {
      pointerId: event.pointerId,
      startDay: day,
      lastDay: day,
      x: event.clientX,
      y: event.clientY,
      dragging: false,
      owner: handlers,
    };
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!drag || !samePointer(event.pointerId)) return;
    beginIfMoved(event.clientX, event.clientY);
  };

  const onPointerEnter = (day: string, event: React.PointerEvent<HTMLButtonElement>) => {
    if (!drag || !samePointer(event.pointerId) || !isDragPointer(event.pointerType)) return;
    const wasDragging = drag.dragging;
    drag.lastDay = day;
    beginIfMoved(event.clientX, event.clientY);
    /* beginIfMoved already extended to this day when it started the drag. */
    if (wasDragging) drag.owner.current.onSelectionExtend?.(day);
  };

  const onClick = (day: string, event: React.MouseEvent<HTMLButtonElement>) => {
    /* detail 0 is a click from the keyboard (Enter / Space): never a drag's. */
    if (swallowClick && event.detail !== 0) {
      swallowClick = false;

      return;
    }
    if (disabledDay(day)) return;
    if (day !== cursor) moveCursor(day, 'pointer', day);
    onDayActivate?.(day);
  };

  /* Focus that arrives on a day without the cursor (Tab into the grid, a
     screen reader's own navigation) moves the cursor there, so the next
     arrow starts from where the user actually is. */
  const onFocus = (day: string) => {
    if (day !== cursor) moveCursor(day, 'focus', day);
  };

  const HeadingTag = headingLevel === 2 ? 'h2' : 'h3';

  return (
    <Root className={className}>
      {showHeading ? (
        <Heading as={HeadingTag} id={headingId}>
          {monthTitle(month)}
        </Heading>
      ) : null}
      <Grid
        ref={gridRef}
        role="grid"
        aria-labelledby={headingId}
        data-month={month}
        data-density={density}
      >
        <Row role="row">
          {WEEKDAYS.map(weekday => (
            <ColumnHeader role="columnheader" aria-label={weekday.long} key={weekday.short}>
              <span aria-hidden="true">{weekday.short}</span>
            </ColumnHeader>
          ))}
        </Row>
        {weeks.map((week, rowIndex) => (
          <Row role="row" key={`week-${rowIndex}`}>
            {week.map((day, col) => {
              /* Cells are keyed by POSITION, not by day: when the parent pages
                 the month, the focused button stays the same element and
                 focus is not dropped to the page. */
              const key = `cell-${rowIndex}-${col}`;
              if (!day) {
                return <EmptyCell role="gridcell" key={key} />;
              }
              const band = bandOf(day, selection);
              const inSelection = band !== 'none';
              const selected =
                !!selection && (day === selection.start || day === selection.end);
              const isToday = day === today;
              const ctx: DayContext = {
                inMonth: monthOf(day) === month,
                isToday,
                weekday: weekdayOf(day),
                selected,
                inSelection,
              };
              const disabled = disabledDay(day);

              return (
                <Cell
                  role="gridcell"
                  key={key}
                  $band={band}
                  aria-selected={selectable ? inSelection : undefined}
                >
                  <DayButton
                    type="button"
                    $density={density}
                    data-day={day}
                    data-today={isToday ? 'true' : undefined}
                    data-edge={selected ? 'true' : undefined}
                    data-outside={ctx.inMonth ? undefined : 'true'}
                    aria-label={dayLabel(day)}
                    aria-disabled={disabled || undefined}
                    aria-current={isToday ? 'date' : undefined}
                    tabIndex={day === tabStop ? 0 : -1}
                    onClick={event => onClick(day, event)}
                    onFocus={() => onFocus(day)}
                    onKeyDown={event => onKeyDown(day, event)}
                    onPointerDown={event => onPointerDown(day, event)}
                    onPointerMove={onPointerMove}
                    onPointerEnter={event => onPointerEnter(day, event)}
                  >
                    {renderDay(day, ctx)}
                  </DayButton>
                </Cell>
              );
            })}
          </Row>
        ))}
      </Grid>
    </Root>
  );
};
